import { useCallback, useEffect, useRef, useState } from 'react'
import {
	positionFromTopLeft,
	positionToTopOffset,
} from '../utils/fitWidgetPosition'

export function useNoteTakerWidgetDrag({
	widgetRef,
	widgetPosition,
	widgetHeight,
	onPositionChange,
}) {
	const [isDragging, setIsDragging] = useState(false)
	const dragStateRef = useRef(null)
	const isDraggingRef = useRef(false)
	const onPositionChangeRef = useRef(onPositionChange)
	onPositionChangeRef.current = onPositionChange
	const widgetPositionRef = useRef(widgetPosition)
	widgetPositionRef.current = widgetPosition

	useEffect(() => {
		const handlePointerMove = event => {
			if (!dragStateRef.current) return

			const { railRect, widgetHeight: height, startLeft, startTop, pointerX, pointerY } =
				dragStateRef.current
			const nextLeft = startLeft + (event.clientX - pointerX)
			const nextTop = startTop + (event.clientY - pointerY)

			onPositionChangeRef.current(
				positionFromTopLeft(
					nextLeft,
					nextTop,
					railRect.width,
					railRect.height,
					height,
					{ preferTopAnchor: true },
				),
			)
		}

		const handlePointerUp = event => {
			if (!dragStateRef.current) return

			const { railRect, widgetHeight: height, captureTarget } = dragStateRef.current
			const current = widgetPositionRef.current
			const top = positionToTopOffset(current, height, railRect.height)

			onPositionChangeRef.current(
				positionFromTopLeft(
					current.left,
					top,
					railRect.width,
					railRect.height,
					height,
				),
			)

			if (captureTarget?.releasePointerCapture) {
				try {
					captureTarget.releasePointerCapture(event.pointerId)
				} catch {
					/* ignore */
				}
			}

			isDraggingRef.current = false
			setIsDragging(false)
			document.body.style.userSelect = ''
			dragStateRef.current = null
		}

		window.addEventListener('pointermove', handlePointerMove)
		window.addEventListener('pointerup', handlePointerUp)
		window.addEventListener('pointercancel', handlePointerUp)

		return () => {
			window.removeEventListener('pointermove', handlePointerMove)
			window.removeEventListener('pointerup', handlePointerUp)
			window.removeEventListener('pointercancel', handlePointerUp)
		}
	}, [])

	const onDragHandlePointerDown = useCallback(
		event => {
			if (!widgetRef.current) return
			const railRect = widgetRef.current.parentElement?.getBoundingClientRect()
			if (!railRect) return

			const position = widgetPositionRef.current
			const startTop = positionToTopOffset(position, widgetHeight, railRect.height)

			isDraggingRef.current = true
			dragStateRef.current = {
				pointerX: event.clientX,
				pointerY: event.clientY,
				startLeft: position.left,
				startTop,
				railRect,
				widgetHeight,
				captureTarget: event.currentTarget,
			}
			setIsDragging(true)
			document.body.style.userSelect = 'none'
			event.currentTarget.setPointerCapture(event.pointerId)
		},
		[widgetHeight, widgetRef],
	)

	return {
		isDragging,
		isDraggingRef,
		onDragHandlePointerDown,
	}
}
